import React from 'react';
import { Box, Container, Stack, Typography, Divider } from '@mui/material';
import Seo from '../SEO/Seo';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" fontWeight={700} sx={{ mb: 1.25 }}>
      {title}
    </Typography>
    <Stack spacing={1.25}>{children}</Stack>
  </Box>
);

const P = ({ children }) => (
  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
    {children}
  </Typography>
);

const Privacy = () => {
  const lastUpdated = 'September 17, 2026';
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://rejectiontales.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Privacy Policy',
    description: 'Privacy Policy for Rejection Tales website and mobile apps.',
    url: `${origin}/privacy`,
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Seo
        title="Privacy Policy"
        description="How Rejection Tales collects, uses, and protects your information on the website and mobile apps."
        path="/privacy"
        schema={schema}
      />

      <Typography variant="h4" component="h1" fontWeight={800} sx={{ mb: 1 }}>
        Privacy Policy
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Last updated: {lastUpdated}
      </Typography>

      <P>
        Rejection Tales (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) provides a website and mobile
        apps that help people process rejection with empathy, community stories, and optional AI
        support (Luna). This Privacy Policy explains what information we collect and how we use it.
      </P>

      <Divider sx={{ my: 3 }} />

      <Section title="1. Information we collect">
        <P>
          <strong>Account information.</strong> When you create an account we may collect your name,
          email address, password (stored hashed), anonymous username for stories, profile photo if
          you sign in with Google, and similar account details.
        </P>
        <P>
          <strong>Content you create.</strong> Stories, comments, journals, check-ins, practice
          progress, and messages you send to Luna.
        </P>
        <P>
          <strong>Technical data.</strong> Device type, app version, approximate usage logs, and
          basic diagnostics needed to run and secure the service.
        </P>
        <P>
          <strong>Optional permissions on mobile.</strong> Microphone and speech recognition (if you
          use talk-to-text), and notifications (if you enable reminders). We do not access these
          unless you grant permission and use those features.
        </P>
      </Section>

      <Section title="2. How we use information">
        <P>We use your information to:</P>
        <P>• Provide and improve the website and apps</P>
        <P>• Authenticate you and keep your account secure</P>
        <P>• Show your stories and community features</P>
        <P>• Power Luna responses and learning tools you choose to use</P>
        <P>• Send optional reminders you enable</P>
        <P>• Respond to support requests and protect against abuse</P>
      </Section>

      <Section title="3. AI support (Luna)">
        <P>
          If you chat with Luna, your messages are sent to our servers and to third-party AI
          providers we use to generate replies. Do not share sensitive information you would not want
          processed by those providers. Luna is supportive tooling, not medical, legal, or crisis
          care.
        </P>
      </Section>

      <Section title="4. Sharing">
        <P>
          We do not sell your personal information. We may share data with service providers that
          help us operate Rejection Tales (hosting, authentication such as Google sign-in, AI
          providers, and email or infrastructure tools), only as needed to provide the service.
        </P>
        <P>
          We may disclose information if required by law, or to protect the rights, safety, and
          integrity of Rejection Tales and our users.
        </P>
        <P>
          Stories you publish may be visible to other users according to the product settings and
          community features you use.
        </P>
      </Section>

      <Section title="5. Data retention and deletion">
        <P>
          We keep account and content data while your account is active. You may delete your account
          in the mobile app (You / Account) or request deletion by contacting support. When you
          delete your account, we remove or anonymize personal data associated with it, except where
          we must retain limited records for security, legal, or operational reasons.
        </P>
      </Section>

      <Section title="6. Children">
        <P>
          Rejection Tales is not directed to children under 13, and we do not knowingly collect
          personal information from children under 13. If you believe a child has provided us data,
          contact us and we will take appropriate steps.
        </P>
      </Section>

      <Section title="7. Security">
        <P>
          We use reasonable technical and organizational measures to protect your information.
          No method of transmission or storage is completely secure.
        </P>
      </Section>

      <Section title="8. Your choices">
        <P>• Update account details where available in the app or website</P>
        <P>• Turn reminders on or off</P>
        <P>• Deny or revoke microphone, speech, or notification permissions in device Settings</P>
        <P>• Delete your account</P>
        <P>
          • Contact us at{' '}
          <Box component="a" href="/support" sx={{ color: 'primary.main' }}>
            /support
          </Box>{' '}
          or through the Contact page
        </P>
      </Section>

      <Section title="9. Changes">
        <P>
          We may update this Privacy Policy from time to time. We will post the updated version on
          this page and revise the &quot;Last updated&quot; date.
        </P>
      </Section>

      <Section title="10. Contact">
        <P>
          Questions about privacy:{' '}
          <Box component="a" href="/support" sx={{ color: 'primary.main' }}>
            rejectiontales.com/support
          </Box>{' '}
          or{' '}
          <Box component="a" href="/contact" sx={{ color: 'primary.main' }}>
            /contact
          </Box>
          .
        </P>
      </Section>
    </Container>
  );
};

export default Privacy;
